import type { accountEn } from './account-en.ts';

type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };

/**
 * Italian strings of the account screens and the legal pages. Typed against the English shape, so
 * a missing or extra key is a compile error. The legal texts are DRAFTS (see account-en.ts).
 */
export const accountIt: DeepString<typeof accountEn> = {
  account: {
    title: 'Account',
    loading: 'Connessione al servizio account',
    unavailable: {
      title: 'Gli account non sono disponibili in questa versione',
      body: 'Il gioco è completo anche senza: gioca da solo, allenati e segui il tutorial. Un account aggiunge profilo, salvataggio nel cloud e, presto, il gioco online.',
    },
    loadFailed: {
      title: 'Il servizio account non risponde',
      body: 'Controlla la connessione e riprova. La tua partita non ne risente.',
      retry: 'Riprova',
    },
    demo: {
      banner:
        'Versione di sviluppo: gli account girano su un backend di prova dentro questa pagina. Nessuna e-mail viene inviata e nulla lascia il tuo dispositivo.',
      mailTitle: 'Casella di posta di prova',
      mailEmpty: 'Nessuna e-mail ancora.',
      mailLast: 'Ultimo codice per {{to}}',
    },
    auth: {
      tabs: { signIn: 'Accedi', signUp: 'Crea account' },
      email: 'E-mail',
      password: 'Password',
      passwordHint: 'Almeno 10 caratteri.',
      newPassword: 'Nuova password',
      code: 'Codice di sei cifre',
      signIn: 'Accedi',
      signUp: 'Crea account',
      forgot: 'Hai dimenticato la password?',
      useCode: 'Usa invece un codice monouso',
      usePassword: 'Usa invece la password',
      sendCode: 'Mandami un codice',
      codeTitle: 'Inserisci il codice',
      codeSent: 'Abbiamo mandato un codice di sei cifre a {{email}}. Vale un’ora.',
      confirmTitle: 'Conferma la tua e-mail',
      verify: 'Conferma',
      resend: 'Manda un nuovo codice',
      resent: 'Un nuovo codice è in arrivo.',
      resetTitle: 'Reimposta la password',
      resetBody:
        'Ti mandiamo un codice via e-mail. Con quello potrai scegliere una nuova password.',
      resetSend: 'Manda il codice',
      setPassword: 'Salva la nuova password',
      passwordSaved: 'Password salvata.',
      providers: 'Oppure continua con',
      provider: { google: 'Google', discord: 'Discord', apple: 'Apple' },
      guestTitle: 'Vuoi solo dare un’occhiata?',
      guestBody:
        'Continua come ospite. Più tardi potrai aggiungere un’e-mail o un provider e tenere tutto.',
      guest: 'Continua come ospite',
      guestBodyOnly:
        'Continua come ospite. Il profilo e i progressi restano su questo dispositivo.',
      guestOnly:
        'Per ora si entra come ospite. L’accesso con e-mail o con Google non è ancora attivo.',
      providersOnly: 'Entra con un account che hai già, oppure continua come ospite.',
      confirmUnavailable:
        'Per ora gli account con e-mail non si possono confermare. Continua come ospite.',
      terms:
        'Prima di salvare qualcosa online ti chiederemo di accettare le Condizioni d’uso e l’Informativa sulla privacy.',
      back: 'Indietro',
      secondFactorTitle: 'Secondo passaggio',
      secondFactorBody:
        'Inserisci il codice di sei cifre mostrato dalla tua app di autenticazione.',
      secondFactorVerify: 'Verifica',
      secondFactorOut: 'Esci',
    },
    overview: {
      guest: 'Ospite',
      guestBannerTitle: 'Stai giocando come ospite',
      guestBannerBody:
        'Aggiungi un’e-mail o un provider per tenere il profilo se cambi dispositivo o ripulisci questo.',
      guestBannerBodyOnly:
        'Il tuo profilo vive su questo dispositivo. Gli account con e-mail o con un provider non sono ancora attivi.',
      guestBannerAction: 'Metti al sicuro il mio account',
      signedInWith: 'Accesso con {{methods}}',
      methodEmail: 'e-mail',
      links: {
        profile: 'Profilo',
        profileHint: 'Soprannome e avatar',
        progress: 'Fedina penale e sfide',
        progressHint: 'Livello, valutazioni, trofei, quello che hai guadagnato',
        security: 'Accesso e sicurezza',
        securityHint: 'E-mail, provider, app di autenticazione',
        privacy: 'Privacy e dati',
        privacyHint: 'Consensi, esportazione, cancellazione',
        admin: 'Area del team',
        adminHint: 'Segnalazioni, giocatori, impostazioni live',
      },
      signOut: 'Esci',
      signOutTitle: 'Uscire?',
      signOutBody: 'Il gioco continua a funzionare. Scegli fin dove uscire.',
      signOutHere: 'Solo questo dispositivo',
      signOutEverywhere: 'Tutti i dispositivi',
      signOutWipe: 'Rimuovi anche i miei progressi da questo dispositivo',
      signOutWipeHint:
        'Usalo su un dispositivo che non è tuo. Il salvataggio nel cloud non viene toccato.',
    },
    consent: {
      title: 'Prima di continuare',
      body: 'Per tenere online il tuo profilo e il tuo salvataggio, e più avanti per giocare con altri, ti chiediamo di leggere e accettare due documenti.',
      terms: 'Accetto le Condizioni d’uso',
      privacy: 'Ho letto l’Informativa sulla privacy',
      read: 'Leggi',
      accept: 'Accetta',
      draft: 'Testi in bozza, in attesa di revisione legale.',
    },
    age: {
      title: 'Quanti anni hai?',
      body: 'Lo usiamo solo per decidere che tipo di chat puoi usare. Non lo vede nessuno.',
      under14: 'Meno di 14',
      from14: 'Da 14 a 17',
      adult: '18 o più',
      confirm: 'Conferma',
      current: 'Fascia d’età: {{group}}',
      locked:
        'Puoi correggerla verso una fascia più giovane. Per passare a una più alta, contatta l’assistenza.',
      notDeclared: 'Non ancora dichiarata: in chat sono disponibili solo le frasi rapide.',
      chat: {
        phrases: 'Chat: solo frasi rapide',
        free: 'Chat: testo libero e frasi rapide',
      },
    },
    sync: {
      title: 'Salvataggio nel cloud',
      off: 'Il salvataggio nel cloud richiede un account con e-mail o provider.',
      needsConsent: 'In attesa che tu accetti i documenti qui sopra.',
      idle: 'Pronto.',
      syncing: 'Sincronizzazione…',
      ok: 'Aggiornato, {{time}}.',
      okNever: 'Aggiornato.',
      error:
        'Sincronizzazione non riuscita. La partita è al sicuro su questo dispositivo; riproveremo.',
      now: 'Sincronizza ora',
      what: 'Impostazioni (tranne la grafica) e progressi ti seguono da un dispositivo all’altro.',
    },
    deletion: {
      pending: 'Il tuo account verrà cancellato il {{date}}.',
      cancel: 'Tieni il mio account',
      cancelled: 'Cancellazione annullata.',
    },
    profile: {
      title: 'Profilo',
      nickname: 'Soprannome',
      nicknameHint: 'Lettere e numeri. Ci viene aggiunto un numero: {{example}}.',
      handle: 'Il tuo identificativo: {{handle}}',
      save: 'Salva',
      saved: 'Salvato.',
      cooldown: 'Puoi cambiare il soprannome una volta a settimana. Prossimo cambio: {{date}}.',
      avatarTitle: 'Avatar',
      avatarRandom: 'Sorprendimi',
      parts: { hat: 'Cappello', mask: 'Maschera', coat: 'Cappotto', rim: 'Luce', suit: 'Emblema' },
      hat: {
        fedora: 'Fedora',
        cap: 'Coppola',
        beret: 'Basco',
        bowler: 'Bombetta',
        headphones: 'Cuffie',
        none: 'Nessuno',
        tophat: 'Cilindro',
        panama: 'Panama',
        hood: 'Cappuccio',
      },
      mask: {
        none: 'Nessuna',
        domino: 'Domino',
        bandana: 'Bandana',
        visor: 'Visiera',
        fox: 'Volpe',
        skull: 'Teschio',
      },
      coat: {
        ink: 'Inchiostro',
        navy: 'Blu notte',
        plum: 'Prugna',
        olive: 'Oliva',
        brown: 'Marrone',
        grey: 'Grigio',
        crimson: 'Cremisi',
        ivory: 'Avorio',
      },
      rim: {
        cyan: 'Ciano',
        amber: 'Ambra',
        green: 'Verde',
        pink: 'Rosa',
        chalk: 'Gesso',
        violet: 'Viola',
        gold: 'Oro',
        ghost: 'Spettro',
      },
      suit: {
        spade: 'Picche',
        heart: 'Cuori',
        diamond: 'Quadri',
        club: 'Fiori',
        star: 'Stella',
        crown: 'Corona',
      },
      moreToUnlock: 'Altri oggetti si sbloccano giocando.',
      toCollection: 'Vedi la collezione',
      nicknameProblem: {
        tooShort: 'Troppo corto.',
        tooLong: 'Troppo lungo.',
        characters: 'Usa solo lettere, numeri, spazi, punti, trattini e trattini bassi.',
        noLetter: 'Includi almeno una lettera.',
        blocked: 'Questo nome non è consentito.',
        reserved: 'Questo nome è riservato.',
      },
      avatarLabel: 'Avatar di {{name}}',
    },
    security: {
      title: 'Accesso e sicurezza',
      methods: 'Metodi di accesso',
      methodGuest: 'Sessione ospite (nessuna e-mail, nessun provider)',
      methodEmail: 'E-mail: {{email}}',
      methodProvider: 'Provider: {{provider}}',
      addEmailTitle: 'Aggiungi un’e-mail e una password',
      addEmailBody: 'Il tuo profilo e i tuoi progressi restano esattamente come sono.',
      addEmailSend: 'Manda il codice di conferma',
      addEmailDone: 'E-mail aggiunta. Ora accedi con quella.',
      linkTitle: 'Collega un altro modo di accedere',
      linkProvider: 'Collega {{provider}}',
      linkHint: 'Andrai sul sito del provider e tornerai qui.',
      passwordTitle: 'Cambia password',
      authenticatorTitle: 'App di autenticazione',
      authenticatorOff:
        'Aggiungi un secondo passaggio all’accesso: un codice di sei cifre da un’app sul telefono.',
      authenticatorOn: 'App di autenticazione: attiva.',
      authenticatorSetup: 'Configura',
      authenticatorScan:
        'Inquadra questa immagine con l’app di autenticazione, oppure digita la chiave. Poi inserisci il codice che mostra.',
      authenticatorKey: 'Chiave',
      authenticatorConfirm: 'Attiva',
      authenticatorRemove: 'Disattiva',
      authenticatorEnabled: 'App di autenticazione attivata.',
      authenticatorRemoved: 'App di autenticazione disattivata.',
      authenticatorQrAlt: 'Codice QR per la tua app di autenticazione',
      authenticatorQrNote:
        'Nella versione di sviluppo questa immagine è un segnaposto: usa la chiave.',
      signOutEverywhere: 'Esci da tutti i dispositivi',
    },
    privacy: {
      title: 'Privacy e dati',
      consentsTitle: 'I tuoi consensi',
      termsLabel: 'Condizioni d’uso',
      privacyLabel: 'Informativa sulla privacy',
      analyticsLabel: 'Statistiche d’uso anonime',
      analyticsBody:
        'Ci aiutano a capire cosa è troppo difficile o rotto. Spente finché non le accendi.',
      granted: 'Accettato, versione {{version}}',
      notGranted: 'Non accettato',
      outdated: 'Una versione più recente richiede la tua attenzione.',
      withdrawNote:
        'Condizioni e Informativa servono per tenere un account. Per ritirarle, cancella l’account.',
      exportTitle: 'I tuoi dati',
      exportBody: 'Scarica come file tutto ciò che conserviamo su di te.',
      exportAction: 'Scarica i miei dati',
      exportDone: 'File pronto.',
      deleteTitle: 'Cancella il mio account',
      deleteBody:
        'Profilo, consensi e salvataggio nel cloud vengono cancellati. I progressi restano su questo dispositivo finché non li rimuovi.',
      deleteAction: 'Cancella il mio account…',
      deleteDialogTitle: 'Cancellare l’account?',
      deleteDialogBody:
        'Puoi tornare indietro entro sette giorni, oppure cancellare tutto subito. La cancellazione immediata è definitiva.',
      deleteLater: 'Cancella tra 7 giorni',
      deleteNow: 'Cancella subito',
      deleteScheduled: 'Cancellazione prevista per il {{date}}.',
      deleteDone: 'Account cancellato.',
    },
    callback: {
      title: 'Accesso in corso',
      body: 'Un momento…',
      failed: 'Non siamo riusciti a completare l’accesso.',
      home: 'Torna all’account',
    },
    errors: {
      invalidCredentials: 'E-mail o password sbagliate.',
      emailNotConfirmed: 'Prima conferma l’e-mail: usa il codice che ti abbiamo mandato.',
      userExists: 'Questa e-mail o questo provider sono già in uso. Prova ad accedere.',
      weakPassword: 'Questa password è troppo debole. Usa almeno 10 caratteri.',
      invalidEmail: 'Questa e-mail non sembra corretta.',
      rateLimited: 'Troppi tentativi. Aspetta un momento e riprova.',
      captchaFailed: 'Il controllo anti-robot non è riuscito. Riprova.',
      codeInvalid: 'Questo codice è sbagliato o scaduto.',
      secondFactorInvalid: 'Questo codice è sbagliato o scaduto.',
      providerDisabled: 'Questo metodo di accesso non è ancora disponibile.',
      signupDisabled: 'Al momento non si possono creare nuovi account.',
      notSignedIn: 'La sessione è terminata. Accedi di nuovo.',
      notConfigured: 'Gli account non sono disponibili in questa versione.',
      network: 'Nessun collegamento col servizio account. Controlla la rete.',
      conflict: 'I tuoi dati sono cambiati altrove. Riprova.',
      nicknameCooldown: 'Hai cambiato il soprannome da poco. Aspetta una settimana.',
      nicknameTaken: 'Questo soprannome è troppo gettonato. Provane un altro.',
      ageLocked: 'Questa fascia d’età può essere cambiata solo dall’assistenza.',
      invalid: 'Qualcosa di quello che hai inserito non è valido.',
      tooLarge: 'È troppo grande per essere salvato.',
      forbidden: 'Non puoi farlo.',
      notFound: 'Non lo troviamo.',
      unknown: 'Qualcosa è andato storto. Riprova.',
    },
  },
  legal: {
    title: 'Note legali',
    draftBanner:
      'Bozza. Questo testo non è ancora stato rivisto da un avvocato e può cambiare prima del rilascio pubblico.',
    updated: 'Versione {{version}}',
    links: {
      terms: 'Condizioni d’uso',
      privacy: 'Informativa sulla privacy',
      deleteAccount: 'Cancella il tuo account',
    },
    terms: {
      title: 'Condizioni d’uso',
      sections: {
        s1: {
          heading: 'Chi siamo e di cosa si tratta',
          body: 'Il Colpo è un gioco cooperativo di rapine per tre-cinque giocatori, online o contro i bot. Queste condizioni valgono per il gioco, il suo sito e le app. [Nome dell’editore, indirizzo e contatti: da compilare a cura dello sviluppatore.]',
        },
        s2: {
          heading: 'Giocare senza account',
          body: 'Puoi giocare da solo, allenarti e seguire il tutorial senza alcun account. Questa parte non richiede le condizioni qui sotto.',
        },
        s3: {
          heading: 'Il tuo account',
          body: 'Un account ti permette di avere un profilo, salvare online impostazioni e progressi e giocare con altri. Tieni riservati i dati di accesso. Sei responsabile di ciò che avviene con il tuo account. Puoi cancellarlo in qualsiasi momento dalla pagina Privacy e dati.',
        },
        s4: {
          heading: 'Come ci aspettiamo che ti comporti',
          body: 'Gioca in modo corretto e sii gentile. Non barare, non molestare gli altri, non usare soprannomi offensivi, non cercare di rompere il gioco o i suoi server. In chat puoi usare solo ciò che la tua fascia d’età consente. Possiamo rimuovere contenuti, limitare funzioni o chiudere gli account che violano queste regole.',
        },
        s5: {
          heading: 'Minori',
          body: 'I giocatori con meno di 14 anni non possono usare la chat a testo libero. [L’età minima per un account e l’eventuale consenso dei genitori dipendono dal Paese: da definire con un avvocato prima del rilascio.]',
        },
        s6: {
          heading: 'Acquisti',
          body: 'In questa versione non ci sono acquisti. Se verranno aggiunti, queste condizioni diranno cosa compri e come funzionano i rimborsi prima che tu paghi.',
        },
        s7: {
          heading: 'Modifiche e cessazione',
          body: 'Possiamo aggiornare queste condizioni. Quando una modifica è importante ti chiederemo di accettare la nuova versione. Puoi smettere di giocare e cancellare l’account quando vuoi. Possiamo sospendere il servizio per manutenzione.',
        },
        s8: {
          heading: 'Responsabilità e legge applicabile',
          body: 'Il gioco è fornito così com’è. [Limiti di responsabilità, legge applicabile e foro competente: da scrivere con un avvocato.]',
        },
      },
    },
    privacy: {
      title: 'Informativa sulla privacy',
      sections: {
        s1: {
          heading: 'Chi è responsabile',
          body: 'Il titolare del trattamento è [nome, indirizzo e contatti dell’editore: da compilare a cura dello sviluppatore]. Puoi scriverci per esercitare i tuoi diritti.',
        },
        s2: {
          heading: 'Cosa conserviamo, e perché',
          body: 'Senza account, il gioco tiene impostazioni e progressi solo sul tuo dispositivo. Con un account conserviamo: la tua e-mail o l’identità del provider con cui accedi, soprannome, avatar e lingua scelta, la fascia d’età che hai dichiarato, le risposte ai consensi e una copia di impostazioni e progressi. Li usiamo per gestire il tuo account, salvare la partita tra dispositivi e, dal rilascio online, per metterti in partita con altri giocatori.',
        },
        s3: {
          heading: 'Base giuridica',
          body: 'Gestire l’account e il salvataggio nel cloud è necessario per fornire il servizio che hai richiesto. Le statistiche d’uso anonime si raccolgono solo se le attivi, e puoi disattivarle in qualsiasi momento. [Basi giuridiche da confermare con un avvocato.]',
        },
        s4: {
          heading: 'Chi tratta i tuoi dati',
          body: 'Usiamo Supabase per ospitare account e dati e Cloudflare Turnstile per distinguere le persone dai robot. Se accedi con Google, Discord o Apple, il provider ci dice chi sei secondo le proprie regole. [Elenco dei responsabili, aree geografiche e trasferimenti fuori dall’UE: da confermare.]',
        },
        s5: {
          heading: 'Per quanto tempo',
          body: 'Conserviamo i tuoi dati finché l’account esiste. Quando lo cancelli eliminiamo profilo, consensi e partita salvata; se hai scelto il ritardo di sette giorni, la cancellazione avviene alla fine, salvo che tu la annulli. [Conservazione di backup e log: da confermare.]',
        },
        s6: {
          heading: 'I tuoi diritti',
          body: 'Dalla pagina Privacy e dati puoi vedere e scaricare i tuoi dati, correggere soprannome e avatar, ritirare i consensi facoltativi e cancellare l’account. Puoi anche contattarci e presentare reclamo al Garante per la protezione dei dati personali.',
        },
        s7: {
          heading: 'Minori',
          body: 'Chiediamo la fascia d’età per decidere quale chat è disponibile. Sotto i 14 anni sono disponibili solo le frasi rapide. [Regole sul consenso dei genitori per Paese: da definire con un avvocato.]',
        },
        s8: {
          heading: 'Cookie e archiviazione',
          body: 'Il gioco salva impostazioni, progressi e sessione di accesso nel browser o nell’app. Servono perché il gioco funzioni. Non usiamo cookie pubblicitari.',
        },
      },
    },
    deleteAccount: {
      title: 'Cancella il tuo account',
      intro:
        'Puoi cancellare in qualsiasi momento il tuo account di Il Colpo e i dati che conserviamo, dal gioco stesso.',
      steps: {
        s1: 'Apri Il Colpo e vai su Account.',
        s2: 'Accedi, poi apri Privacy e dati.',
        s3: 'Scegli Cancella il mio account e decidi se farlo tra sette giorni o subito.',
      },
      what: 'Cosa viene cancellato: profilo, consensi e salvataggio nel cloud. Cosa resta: i progressi salvati sul tuo dispositivo, finché non li rimuovi dal gioco.',
      open: 'Apri Privacy e dati',
      contact:
        '[Contatto di assistenza per chi non riesce più ad accedere: da compilare a cura dello sviluppatore.]',
    },
  },
};
